import React from "react"
import { cn } from "../../../lib/utils"
import { ChevronDown, ChevronUp } from "../Icon"

/**
 * FilterChip — Shadcn-based chip button for FilterChipBar.
 *
 * Replaces the legacy `.chip-btn` markup that accumulated 6 patches for
 * centering issues caused by PrimeIcon interactions and Bootstrap removal.
 * Tailwind utilities guarantee correct flex centering without overrides.
 *
 * forwardRef is required so Radix Popover.Trigger asChild can attach its
 * ref and ARIA attributes to the underlying <button> element.
 *
 * @param {string}   label        - Chip label text (may include count suffix)
 * @param {boolean}  active       - Activates the slate bg / white text state
 * @param {boolean}  hasDropdown  - Renders ChevronDown/Up icon on the right
 * @param {boolean}  isOpen       - When hasDropdown, switches to ChevronUp
 * @param {function} onClick      - Click handler
 * @param {string}   [className]  - Optional additional classes
 */
const FilterChip = React.forwardRef(function FilterChip(
  { label, active, hasDropdown, isOpen, onClick, className, chipKey, ...props },
  ref,
) {
  const Chevron = isOpen ? ChevronUp : ChevronDown

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      {...(chipKey === undefined
        ? {}
        : { "data-testid": `filter-chip-${chipKey}` })}
      // touch-action:manipulation removes the 300ms tap delay and prevents
      // the browser from interpreting a chip tap as a scroll gesture on mobile.
      style={{ touchAction: "manipulation" }}
      className={cn(
        // Base layout — Tailwind handles centering; no PrimeIcon interference
        "inline-flex items-center justify-center gap-1 h-8 px-2.5",
        "text-xs font-semibold whitespace-nowrap",
        "rounded-[var(--radius-lg)] border",
        "cursor-pointer transition-colors duration-150",
        // Default state — transparent bg, green outline (unselected = bordered)
        "bg-transparent text-[var(--slate)] border-[var(--brand-green)]",
        // Hover (overridden by active below when active=true)
        "hover:bg-[var(--green-tint-light)] hover:border-[var(--brand-green)]",
        // Active state — filled green tint bg, green border (selected = filled)
        active &&
          "bg-[var(--green-tint-light)] text-[var(--slate)] border-[var(--brand-green)] font-semibold hover:bg-[var(--green-tint)] hover:border-[var(--brand-green)]",
        // Open state — subtle focus ring when popover is visible
        isOpen && "shadow-[0_0_0_2px_var(--green-tint)]",
        className,
      )}
      {...props}
    >
      {label}
      {hasDropdown && <Chevron size={12} className="opacity-80 shrink-0" />}
    </button>
  )
})

export default FilterChip
